/**
 * services/messages-service.js — load + paginate message data into the store.
 *
 * #122: This file is now scoped to the read/paginate side of the message
 * pipeline. All outbound send helpers (sendText, sendMedia, sendLocation,
 * sendContact, sendReaction, markUnreadAsRead) moved to
 * `messages-outbound.js` where they can be read without scrolling past
 * a few hundred lines of loading boilerplate.
 *
 * For backwards compatibility, every outbound export is re-exported from
 * here so existing `import { sendText } from './messages-service.js'`
 * call sites keep working without a churning rename.
 */

import * as api from './api.js';
import {
  setMessages,
  prependMessages,
  addReaction,
} from '../stores/messages.js';
import { $openChatChannel } from '../stores/ui.js';
import { $chats } from '../stores/chats.js';
import { DIRECTION } from '../shared/constants.js';

// Re-export every outbound helper so consumers that import from
// messages-service.js don't break. New code can import directly from
// messages-outbound.js.
export {
  sendText,
  sendLocation,
  sendContact,
  sendMedia,
  sendReaction,
  markUnreadAsRead,
} from './messages-outbound.js';

// ── Pagination state ────────────────────────────────────────────────────────
// Per-thread cursor tracking — shared by loadMessages (reset on open) and
// loadOlderMessages (advance on scroll). Each entry: { nextPage, done, loading }.
const _paginationState = new Map();

/** Normalize a message from the proxy response into our store shape */
function normalizeMessage(raw) {
  return {
    id:         raw.wamid || raw.id,
    wamid:      raw.wamid,
    metaWamid:  raw.meta_wamid || null,
    type:       raw.type || 'text',
    direction:  raw.direction,
    fromMe:     raw.from_me || raw.direction === DIRECTION.OUTBOUND,
    status:     raw.status,
    timestamp:  raw.timestamp,
    fromPhone:  raw.from,
    toPhone:    raw.to,
    agentName:  raw.agent_name,
    replyTo:    raw.context?.message_id || null,
    // Spread message-type-specific fields (text.body, image.link, etc.)
    text:       raw.text,
    image:      raw.image,
    video:      raw.video,
    audio:      raw.audio,
    document:   raw.document,
    location:   raw.location,
    interactive: raw.interactive,
    template:   raw.template,
    reaction:   raw.reaction,
  };
}

/**
 * Load messages for a thread into the store.
 *
 * #153: the caller now passes the channelPhoneId for the per-channel chat
 * row being opened. Messages are scoped server-side to that channel so a
 * contact with history on two channels shows only the thread for the row
 * you clicked. The old "auto-detect channel from last inbound" logic is
 * gone — the row already carries its channel, pinned at chat-list load.
 *
 * The chatId here is the composite chat_key (contactId:channelId). We
 * key $threads and pagination by phone since the backend API still reads
 * inbox_messages by chat_id=phone; the channel filter is layered on top.
 */
export async function loadMessages(chatId, phone, channelPhoneId = null) {

  // Reset pagination cursor — a fresh thread open starts over at page 1,
  // next load-older will fetch page 2.
  _paginationState.set(phone, { nextPage: 2, done: false, loading: false, channelPhoneId });

  // Pin the reply channel BEFORE the fetch so the composer and any early
  // interaction (type-while-loading) bind to the row's channel. Previously
  // this ran AFTER the fetch, so a send initiated before load completion
  // would fall through to whatever $openChatChannel held from the last row.
  $openChatChannel.set(channelPhoneId || null);

  // Pre-populate from cached chat row if we somehow got called without a
  // channelPhoneId (shouldn't happen post-#153, but keeps old code paths safe).
  if (!channelPhoneId) {
    const cachedChat = $chats.get()[chatId];
    if (cachedChat?.channelPhoneId) {
      $openChatChannel.set(cachedChat.channelPhoneId);
    }
  }

  // Initial page: 50 messages so a chat with active recent traffic doesn't
  // truncate visible history at "20 most recent". Older messages still
  // load via scroll-up pagination (loadOlderMessages, also limit:50).
  const data = await api.fetchMessages(chatId, phone, {
    page: 1,
    limit: 50,
    sort: 'newest',
    channelPhoneId,
  });
  const rawMsgs = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);

  // Reverse to oldest-first (proxy returns newest-first)
  const allMessages = rawMsgs.map(normalizeMessage).reverse();

  // Extract reactions into the $reactions store. Reactions are persisted as
  // inbox_messages rows but rendered as overlays on their target bubbles, so
  // they belong in the $reactions map — NOT in the thread message array.
  // Without this extraction, historical reactions on chat open are invisible
  // (the realtime path handled new reactions, but loadMessages didn't until
  // this fix). Bug found during Phase 1 testing on 2026-04-14.
  const threadMessages = [];
  let reactionCount = 0;
  for (const msg of allMessages) {
    if (msg.type === 'reaction') {
      const targetWamid = msg.replyTo || msg.reaction?.message_id || null;
      if (targetWamid) {
        // S5: reaction store keyed by (phone, channel).
        addReaction(phone, channelPhoneId, targetWamid, {
          emoji:     msg.reaction?.emoji || '',
          fromMe:    msg.fromMe,
          fromPhone: msg.fromPhone || null,
          timestamp: msg.timestamp,
          wamid:     msg.wamid,
        });
        reactionCount++;
      }
      // Don't include in the thread bubble list
      continue;
    }
    threadMessages.push(msg);
  }
  if (reactionCount > 0) {
  }

  // S5: composite-key thread so two channels sharing the same phone
  // don't collide on a single array.
  setMessages(phone, channelPhoneId, threadMessages);
  return threadMessages;
}

// ── Pagination: load-older ───────────────────────────────────────────────────
//
// Entry shape:
//   - nextPage: page number to fetch next (starts at 2 — page 1 is the
//     initial load from loadMessages)
//   - done:     true once the server returned fewer than `limit` rows, i.e.
//     we've seen the oldest message in the thread — don't ping the API again
//   - loading:  true while a fetch is in flight; guards against double-firing
//     when the user bounces at the top of the scroll container

function _getPaginationState(phone) {
  if (!_paginationState.has(phone)) {
    _paginationState.set(phone, { nextPage: 2, done: false, loading: false, channelPhoneId: null });
  }
  return _paginationState.get(phone);
}

/** Reset pagination cursor — called when a thread is freshly loaded. */
export function resetPagination(phone) {
  _paginationState.set(phone, { nextPage: 2, done: false, loading: false, channelPhoneId: null });
}

/** Snapshot read so Thread can show/hide the "loading older…" spinner. */
export function getPaginationState(phone) {
  return { ..._getPaginationState(phone) };
}

/**
 * Load the next older page of messages and prepend them to the store.
 * Returns the number of NEW messages actually added (0 if end reached or
 * an in-flight call is already happening). The Thread component uses the
 * count to decide whether to keep the "load older" indicator visible and
 * to skip adjustment when zero new rows came back.
 */
export async function loadOlderMessages(chatId, phone) {
  const state = _getPaginationState(phone);
  if (state.done || state.loading) return 0;
  state.loading = true;
  try {
    // #153: carry the channel filter through pagination so scrolling up in
    // Sambit's Prinstaindia thread keeps loading Prinstaindia messages
    // instead of leaking the ODIV channel's history into the view.
    const data = await api.fetchMessages(chatId, phone, {
      page: state.nextPage,
      limit: 50,
      sort: 'newest',
      channelPhoneId: state.channelPhoneId || null,
    });
    const rawMsgs = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);

    // Page was short — we've hit the end of the thread.
    if (rawMsgs.length < 50) state.done = true;

    // Proxy returns newest-first; after reverse() the array is oldest-first
    // which matches the thread render order.
    const olderMessages = rawMsgs.map(normalizeMessage).reverse();

    // Extract historical reactions same as loadMessages — they belong in
    // $reactions, not in the thread bubble list.
    // S5: addReaction + prependMessages now composite-keyed. Pull the
    // channel from the pagination state captured on thread open.
    const channelForPage = state.channelPhoneId || null;
    const threadAdd = [];
    for (const msg of olderMessages) {
      if (msg.type === 'reaction') {
        const targetWamid = msg.replyTo || msg.reaction?.message_id || null;
        if (targetWamid) {
          addReaction(phone, channelForPage, targetWamid, {
            emoji:     msg.reaction?.emoji || '',
            fromMe:    msg.fromMe,
            fromPhone: msg.fromPhone || null,
            timestamp: msg.timestamp,
            wamid:     msg.wamid,
          });
        }
        continue;
      }
      threadAdd.push(msg);
    }

    // Advance the cursor BEFORE prepending — if the user scrolls fast we
    // want the next trigger to fetch page N+1, not re-fetch page N.
    state.nextPage += 1;

    prependMessages(phone, channelForPage, threadAdd);
    return threadAdd.length;
  } catch (e) {
    console.warn('[v2 messages] loadOlderMessages failed:', e.message);
    return 0;
  } finally {
    state.loading = false;
  }
}

// sendTemplate() removed in #100 — agent inbox no longer offers template
// re-engagement. The product position: agents respond to active 24h-window
// customers; outbound re-engagement is owned by marketing surfaces. If we
// ever need this back, git history (8ad5325..) has the full
// implementation including WABA scoping, header media handling, and
// preview-bubble support.
